import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { AppDispatch, RootState } from "../../../../store/store";
import { STATUS } from "../../../../store/slices/OrganizationSlice";
import { fetchSupportDebugSafeToSpendAsync } from "../../../../store/thunks/SupportDebugThunk";
import { SupportDebugBill } from "../../../../services/SupportDebugService";
import StatCard from "../../../common/StatCard";
import BillsTable from "../BillsTable";
import { PanelErrorCard, PanelLoadingCard } from "../PanelStates";
import { formatCurrency, formatDate } from "../utils";

interface SafeToSpendPanelProps {
  userId: string;
}

// Card with a chevron toggle that reveals a bills table.
const CollapsibleBillsCard = ({ title, bills }: { title: string; bills: SupportDebugBill[] }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
      <button
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        className="flex w-full items-center justify-between px-6 pb-4 text-left"
        aria-expanded={open}
      >
        <span className="desktext11 text-texthead">
          {title} ({bills.length})
        </span>
        {open ? (
          <ChevronUpIcon className="h-5 w-5 text-zinc-500" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-zinc-500" />
        )}
      </button>
      {open && <BillsTable bills={bills} emptyMessage="No bills in this list" />}
    </div>
  );
};

const SafeToSpendPanel = ({ userId }: SafeToSpendPanelProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { data, status, error } = useSelector(
    (state: RootState) => state.SupportDebugStore.safeToSpend
  );

  useEffect(() => {
    if (userId && status === STATUS.IDLE) {
      dispatch(fetchSupportDebugSafeToSpendAsync({ userId }));
    }
  }, [userId, status, dispatch]);

  if (status === STATUS.LOADING || status === STATUS.IDLE) {
    return <PanelLoadingCard />;
  }

  if (status === STATUS.FAILED) {
    return <PanelErrorCard error={error} />;
  }

  if (!data) {
    return null;
  }

  const { formula, raw } = data;

  return (
    <>
      {/* Hero number */}
      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <StatCard
          title="Safe to Spend"
          description="The number the app shows this user"
          value={formatCurrency(raw.safeToSpend)}
          valueLabel={`as of ${formatDate(data.asOf)}`}
        />
      </div>

      {/* How it's calculated */}
      <div className="mt-6 w-full rounded-lg border-2 border-shadowborder pt-5">
        <div className="px-6">
          <div className="desktext11 text-texthead">How it&rsquo;s calculated</div>
          <p className="mt-1 font-mono text-xs text-textColor3">{formula.expression}</p>
        </div>

        {/* Subtraction visual: income − upcomingBills − alreadySpent = safeToSpend */}
        <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-4 px-6">
          {formula.components.map((component, index) => (
            <Fragment key={component.key}>
              {index > 0 && <span className="text-xl text-textColor3">&minus;</span>}
              <div className="text-center">
                <div className="text-lg font-semibold tabular-nums text-texthead">
                  {formatCurrency(component.value)}
                </div>
                <div className="text-xs text-textColor">{component.label}</div>
              </div>
            </Fragment>
          ))}
          <span className="text-xl text-textColor3">=</span>
          <div className="text-center">
            <div className="text-lg font-bold tabular-nums text-texthead">
              {formatCurrency(formula.result.value)}
            </div>
            <div className="text-xs text-textColor">Safe to Spend</div>
          </div>
        </div>

        {/* Component rows */}
        <div className="mt-6 divide-y divide-shadowborder border-t border-shadowborder">
          {formula.components.map((component) => (
            <div key={component.key} className="flex items-start justify-between gap-6 px-6 py-4">
              <div className="min-w-0">
                <div className="text-sm font-medium text-texthead">{component.label}</div>
                {component.window && (
                  <div className="mt-0.5 text-xs text-textColor3">
                    {formatDate(component.window.start)} &rarr; {formatDate(component.window.end)}
                  </div>
                )}
                {component.description && (
                  <p className="mt-1 max-w-xl text-sm text-textColor">{component.description}</p>
                )}
              </div>
              <div className="shrink-0 text-right text-sm font-semibold tabular-nums text-texthead">
                {formatCurrency(component.value)}
              </div>
            </div>
          ))}
          <div className="flex items-start justify-between gap-6 px-6 py-4">
            <div className="text-sm font-semibold text-texthead">Safe to Spend</div>
            <div className="shrink-0 text-right text-base font-bold tabular-nums text-texthead">
              {formatCurrency(formula.result.value)}
            </div>
          </div>
        </div>
      </div>

      {!raw.hasActiveRecurringBills && (
        <div className="mt-6 flex items-start gap-3 rounded-lg border-2 border-amber-200 bg-amber-50 p-4">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-500" />
          <p className="text-sm text-amber-800">
            No active recurring bills were detected for this user — the upcoming-bills component
            is built from an empty set.
          </p>
        </div>
      )}

      <CollapsibleBillsCard title="Upcoming bills (rest of month)" bills={raw.upcomingBillsList} />
      <CollapsibleBillsCard
        title="Upcoming bills (next 30 days)"
        bills={raw.upcomingBillsListForNext30Days}
      />
    </>
  );
};

export default SafeToSpendPanel;
